import React, { useEffect, useState } from "react";
import { logError } from "../lib/log";
import { api } from "../lib/api";
import { Beaker, Flame, Wind, Sparkles, ThermometerSun, ShieldAlert, Play } from "lucide-react";

const BUBBLES = Array.from({ length: 18 }, (_, i) => ({ id: `b${i}`, left: 5 + Math.random() * 90, size: 6 + Math.random() * 12, dur: 2 + Math.random() * 2, delay: Math.random() * 2 }));
const CRYSTALS = Array.from({ length: 8 }, (_, i) => ({ id: `c${i}`, delay: i * 0.1 }));

function VisualCue({ visual }) {
  if (!visual) return null;
  const cues = [];
  if (visual.flame) cues.push({ icon: Flame, label: "Flame", color: "text-orange-300" });
  if (visual.bubbles) cues.push({ icon: Wind, label: "Bubbles", color: "text-cyan-300" });
  if (visual.gas) cues.push({ icon: Wind, label: "Gas", color: "text-slate-300" });
  if (visual.heat) cues.push({ icon: ThermometerSun, label: "Heat", color: "text-amber-300" });
  if (visual.colorChange) cues.push({ icon: Sparkles, label: "Color change", color: "text-fuchsia-300" });
  if (visual.crystals) cues.push({ icon: Sparkles, label: "Crystals", color: "text-lime-300" });
  return (
    <div className="flex flex-wrap gap-2">
      {cues.map((c) => (
        <span key={c.label} className={`inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[11px] ${c.color}`}>
          <c.icon size={12} /> {c.label}
        </span>
      ))}
    </div>
  );
}

function ReactionAnimation({ visual, playing }) {
  const color = visual?.color || "#22d3ee";
  return (
    <div className="relative h-56 overflow-hidden rounded-md border border-white/10 bg-gradient-to-b from-black/40 to-black/70" data-testid="reaction-canvas">
      <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: `linear-gradient(180deg, transparent, ${color}44)` }} />
      {playing && visual?.flame && (
        <div className="absolute bottom-0 left-1/2 h-40 w-24 -translate-x-1/2 animate-pulse" style={{ background: `radial-gradient(ellipse at bottom, ${color}, transparent 70%)` }} />
      )}
      {playing && visual?.bubbles && (
        <>
          {BUBBLES.map((b) => (
            <span
              key={b.id}
              className="absolute bottom-0 rounded-full opacity-70"
              style={{ left: `${b.left}%`, width: b.size, height: b.size, background: `${color}bb`, animation: `rise ${b.dur}s linear ${b.delay}s infinite` }}
            />
          ))}
        </>
      )}
      {playing && visual?.crystals && (
        <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1">
          {CRYSTALS.map((c) => (
            <div key={c.id} className="h-8 w-1 rotate-3 bg-slate-100" style={{ animation: `grow 1.2s ${c.delay}s ease-out both` }} />
          ))}
        </div>
      )}
      <style>{`
        @keyframes rise { from { transform: translateY(0); opacity: .8 } to { transform: translateY(-220px); opacity: 0 } }
        @keyframes grow { from { transform: scaleY(0) } to { transform: scaleY(1) } }
      `}</style>
      <div className="absolute inset-x-0 bottom-2 text-center text-xs text-slate-500">Beaker</div>
    </div>
  );
}

export default function Simulator() {
  const [reactions, setReactions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [balanced, setBalanced] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.get("/reactions").then((r) => setReactions(r.data.reactions)).catch(() => {});
  }, []);

  const play = async (r) => {
    setCurrent(r);
    setBalanced(null);
    setPlaying(true);
    try {
      const { data } = await api.post("/balancer", { equation: r.equation });
      setBalanced(data);
    } catch (e) {
      logError("Balance failed", e);
      setBalanced({ error: "Could not balance this equation." });
    }
    setTimeout(() => setPlaying(false), 4200);
  };

  return (
    <div className="space-y-8" data-testid="simulator-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-xs uppercase tracking-widest text-fuchsia-300">
          <Beaker size={12} /> Reaction Simulator
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Watch chemistry happen.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">Pick a reaction, hit play. See the balanced equation and simulated visual cues — flames, bubbles, crystals.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="space-y-3">
          {reactions.map((r) => (
            <button
              key={r.id}
              onClick={() => play(r)}
              data-testid={`reaction-${r.id}`}
              className={`w-full rounded-md border p-4 text-left transition-colors ${
                current?.id === r.id ? "border-fuchsia-400/50 bg-fuchsia-500/10" : "border-white/10 bg-black/40 hover:border-fuchsia-400/30"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-white">{r.name}</div>
                <Play size={14} className="text-fuchsia-300" />
              </div>
              <div className="font-mono-num mt-1 text-xs text-slate-400">{r.equation}</div>
              <div className="mt-3"><VisualCue visual={r.visual} /></div>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <ReactionAnimation visual={current?.visual} playing={playing} />
          {current ? (
            <div className="rounded-md border border-white/10 bg-black/40 p-5" data-testid="reaction-detail">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Now playing</div>
              <div className="font-display mt-1 text-xl font-semibold text-white">{current.name}</div>
              {balanced && (
                <div className={`font-mono-num mt-3 rounded-md border p-3 ${balanced.error ? "border-red-400/40 bg-red-500/5 text-red-200" : "border-lime-400/40 bg-lime-500/5 text-lime-200"}`} data-testid="reaction-balanced">
                  {balanced.error || balanced.balanced}
                </div>
              )}
              <p className="mt-4 text-sm text-slate-300">{current.description}</p>
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-400/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                <ShieldAlert size={14} className="mt-0.5" /> <span>{current.safety}</span>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-white/10 bg-black/40 p-5 text-sm text-slate-500">Pick a reaction to play →</div>
          )}
        </div>
      </div>
    </div>
  );
}
